/**
 * <custom-build-hero> — bike-build hero slideshow.
 * Progressive enhancement over a scroll-snap track:
 *   - prev/next arrows, active-dot sync, "n / total" counter
 *   - Fullscreen API toggle (image contained to the viewport, controls overlaid)
 *   - keyboard (arrows) + swipe already handled natively by scroll-snap
 * Degrades gracefully: without JS the track still scroll-snaps and the dot
 * anchor links still jump between slides.
 */
class CustomBuildHero extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-hero-slide]'));
    if (!this.track || this.slides.length === 0) return;

    this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-hero-dot]'));
    this.prevBtn = this.querySelector('[data-hero-prev]');
    this.nextBtn = this.querySelector('[data-hero-next]');
    this.fsBtn = this.querySelector('[data-hero-fullscreen]');
    this.counter = this.querySelector('[data-hero-current]');
    this.index = 0;

    // Reveal JS-only controls now that the script is running.
    [this.prevBtn, this.nextBtn, this.fsBtn].forEach(function (el) {
      if (el) el.hidden = false;
    });

    var self = this;

    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.go(-1); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.go(1); });

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function (e) {
        e.preventDefault();
        self.scrollToIndex(parseInt(dot.dataset.index, 10) || 0);
      });
    });

    // Track which slide is centered → drives dots + counter.
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            var i = self.slides.indexOf(entry.target);
            if (i > -1) self.setActive(i);
          }
        });
      }, { root: this.track, threshold: [0.5, 0.75] });
      this.slides.forEach(function (s) { self.observer.observe(s); });
    } else {
      this.track.addEventListener('scroll', function () {
        var i = Math.round(self.track.scrollLeft / self.track.clientWidth);
        self.setActive(i);
      }, { passive: true });
    }

    // Fullscreen
    if (this.fsBtn) {
      this.fsBtn.addEventListener('click', function () { self.toggleFullscreen(); });
      document.addEventListener('fullscreenchange', function () { self.syncFullscreen(); });
      document.addEventListener('webkitfullscreenchange', function () { self.syncFullscreen(); });
    }

    // Keyboard nav when the slideshow (or its track) has focus, or in fullscreen.
    this.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); self.go(1); }
      else if (e.key === 'Escape' && self.isFullscreen()) { self.exitFullscreen(); }
    });

    this.setActive(0);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  scrollToIndex(i) {
    var n = this.slides.length;
    i = Math.max(0, Math.min(i, n - 1));
    var target = this.slides[i];
    if (!target) return;
    this.track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    this.setActive(i);
  }

  go(delta) {
    var n = this.slides.length;
    this.scrollToIndex((this.index + delta + n) % n);
  }

  setActive(i) {
    if (i === this.index && this.dots.length && this.dots[i] && this.dots[i].classList.contains('is-active')) return;
    this.index = i;
    this.dots.forEach(function (dot, di) {
      dot.classList.toggle('is-active', di === i);
      if (di === i) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (this.counter) this.counter.textContent = String(i + 1);
  }

  isFullscreen() {
    return document.fullscreenElement === this || document.webkitFullscreenElement === this;
  }

  toggleFullscreen() {
    if (this.isFullscreen()) this.exitFullscreen();
    else this.enterFullscreen();
  }

  enterFullscreen() {
    var req = this.requestFullscreen || this.webkitRequestFullscreen;
    if (req) {
      var p = req.call(this);
      if (p && p.catch) p.catch(function () {});
    }
  }

  exitFullscreen() {
    var exit = document.exitFullscreen || document.webkitExitFullscreen;
    if (exit) exit.call(document);
  }

  syncFullscreen() {
    var on = this.isFullscreen();
    this.classList.toggle('is-fullscreen', on);
    if (this.fsBtn) {
      this.fsBtn.setAttribute('aria-label', on ? 'Exit full screen' : 'View photos full screen');
      var openIcon = this.fsBtn.querySelector('.custom-build-hero__fs-icon--open');
      var closeIcon = this.fsBtn.querySelector('.custom-build-hero__fs-icon--close');
      if (openIcon) openIcon.hidden = on;
      if (closeIcon) closeIcon.hidden = !on;
    }
    // Re-align to the current slide after the viewport resizes.
    var self = this;
    requestAnimationFrame(function () {
      var target = self.slides[self.index];
      if (target) self.track.scrollTo({ left: target.offsetLeft, behavior: 'auto' });
    });
  }
}

if (!customElements.get('custom-build-hero')) {
  customElements.define('custom-build-hero', CustomBuildHero);
}
